import './date-extensions';
import { dateConvert } from './date-operations';
import { GlobalConstants } from './global-constants';
import { ReportingPeriods } from './reporting-periods';
import { ReportingPeriodNameRanks, ReportingPeriodNames } from './reporting-period-names';
import { EntityTypes } from './entity-types';
import { EntityTypeNames } from './entity-type-names';
import { CDSCodeTypes } from "./cds-code-types";
import { ArtifactTypes, ArtifactTypeLabels } from "./artifact-types";

export const fieldof = <T>(key: keyof T) => key;

export function localeCompare(string1: string, string2: string, emptySameAsNull: boolean = false): number {
  if (!string1 || (emptySameAsNull && string1.length === 0)) {
    return !string2 || (emptySameAsNull && string2.length === 0) ? 0 : -1;
  }
  return !string2 || (emptySameAsNull && string2.length === 0) ? +1 : string1.localeCompare(string2);
}

export function formatCDSCode(cdsCode: string): string {
  return cdsCode
    ? `${cdsCode.slice(0, 2)}-${cdsCode.slice(2, 7)}-${cdsCode.slice(7)}`
    : '';
}

export function dateFormat(value: string | number | Date, format?: string): string {
  value = dateConvert(value);
  return value
    ? value.format(format || 'MM-DD-YY')
    : '(none)';
}

export function timeFormat(value: string | number | Date, format?: string): string {
    const dateValue = new Date(value);
    const timeValue = dateValue.getTime();
    if (!isNaN(timeValue) && timeValue !== GlobalConstants.minEpoch)
      return dateValue.format(format || 'HH:mm');
}

export function dateTimeFormat(value: string | number | Date): string {
  if (value) {
    const dateValue = new Date(value);
    const timeValue = dateValue.getTime();
    if (!isNaN(timeValue) && timeValue !== GlobalConstants.minEpoch)
      return dateValue.format('MM-DD-YY HH:mm');
  }
  return '(none)';
}

export function dateTimeWithSecondsFormat(value: string | number | Date): string {
  if (value) {
    const dateValue = new Date(value);
    const timeValue = dateValue.getTime();
    if (!isNaN(timeValue) && timeValue !== GlobalConstants.minEpoch)
      return dateValue.format('MM-DD-YY hh:mm:ss');
  }
  return '(none)';
}

export function emptyStringFormat(): string { return ''; }
export function stringFormat(value: string): string { return value ? value : ''; }
export function stringFormat2Decimals(value: string): string { return value ? (+value).toFixed(2) : ''; }
export function numberFormat2Decimals(value: number): string { return value ? (+value).toFixed(2) : '0.00'; }
export function booleanCheckedUncheckedFormat(value: boolean): string { return value ? 'Checked' : 'Unchecked'; }
export function booleanYesNoFormat(value: boolean): string { return value ? 'Yes' : value === false ? 'No' : ''; }
export function booleanYesNoNotEmptyFormat(value: boolean): string { return value ? 'Yes' : 'No' }
export function booleanApprovedDisapproved(value: boolean): string { return value ? 'Approved' : value === false ? 'Disapproved' : ''; }

export function getReportingPeriodLabel(value: ReportingPeriods): string {
  const reportingPeriodNames = new ReportingPeriodNames();
  return (value && reportingPeriodNames[value]) || '';
}

export function getReportingPeriodLabelFromRank(rank: number): string {
  const reportingPeriodNameRanks = new ReportingPeriodNameRanks();
  return (rank && reportingPeriodNameRanks[rank]) || '';
}

export function getEntityTypeLabel(value: EntityTypes): string {
  const entityTypeNames = new EntityTypeNames();
  return (value && entityTypeNames[value]) || '';
}

export function parseCDSCode(cdsCode: string, cdsCodeType: CDSCodeTypes): string {
  switch (cdsCodeType) {
    case CDSCodeTypes.County:
      return cdsCode.substring(0, 2);
    case CDSCodeTypes.District:
      return cdsCode.substring(2, 7);
    case CDSCodeTypes.School:
      return cdsCode.substring(7, 14);
    default:
      return '';
  }
}

export function getPropertyValue(obj: any, propertyPath: string): any {
  return propertyPath.split('.').reduce((acc, curr) => acc && acc[curr], obj);
}

export function capitalize(str: string): string {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

export function camelize(str: string, separator: string = '.'): string {
  return str.split(separator).reduce((acc, curr, index) => `${acc}${index === 0 ? curr : capitalize(curr)}`, '');
}

export function trimStartChars(value: string, chars: string) {
  if (chars === undefined)
    chars = '\s';

  return value.replace(new RegExp(`^[${chars}]+`), '');
}

export function trimEndChars(value: string, chars: string) {
  if (chars === undefined)
    chars = '\s';

  return value.replace(new RegExp(`[${chars}]+$`), '');
}

export function trimChars(value: string, chars: string) {
  const result = trimStartChars(value, chars);
  return trimEndChars(result, chars);
}

export function formatCommaSeparatedList(values: Array<string>): string {
  return values ? values.join(', ') : '';
}

export function getArtifactTypeLabel(value: ArtifactTypes): string {
  const artifactTypeLabels = new ArtifactTypeLabels();
  return (value && artifactTypeLabels[value]) || '';
}
